import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { 
  LayoutDashboard, 
  FileText, 
  CheckSquare, 
  MessageSquare, 
  ArrowUpDown, 
  Users, 
  Settings, 
  LogOut,
  Building2,
  MailCheck
} from 'lucide-react';

const Layout = () => {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [clientConfig, setClientConfig] = useState(null);

  useEffect(() => {
    // Fetch client config for logo and branding
    const fetchConfig = async () => {
      try {
        const response = await api.get('/config');
        setClientConfig(response.data.config);
        
        // Apply primary color dynamically
        const primaryColor = response.data.config?.primaryColor || response.data.config?.primary_color;
        if (primaryColor) {
          document.documentElement.style.setProperty('--primary', primaryColor);
          // Calculate darker/lighter variants
          const darkColor = adjustColor(primaryColor, -15);
          const lightColor = adjustColor(primaryColor, 20);
          document.documentElement.style.setProperty('--primary-dark', darkColor);
          document.documentElement.style.setProperty('--primary-light', lightColor);
        }
      } catch (err) {
        console.error('Failed to fetch config:', err);
      }
    };
    fetchConfig();
  }, []);

  // Helper to darken/lighten a hex color
  const adjustColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, Math.min(255, (num >> 16) + amt));
    const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
    const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
    return `#${(1 << 24 | R << 16 | G << 8 | B).toString(16).slice(1)}`;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/po-lines', icon: FileText, label: 'PO Lines' },
    { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
    { to: '/chat', icon: MessageSquare, label: 'Chat Assistant' },
    { to: '/response-check', icon: MailCheck, label: 'Response Check' },
    { to: '/data-exchange', icon: ArrowUpDown, label: 'Data Exchange', adminOnly: true },
    { to: '/users', icon: Users, label: 'Users', adminOnly: true },
    { to: '/admin', icon: Building2, label: 'Clients', superAdminOnly: true },
    { to: '/settings', icon: Settings, label: 'Settings' }
  ];

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          {/* Logo and Client Name on same line */}
          <div className="flex items-center gap-2">
            {clientConfig?.logoUrl ? (
              <img 
                src={clientConfig.logoUrl} 
                alt="Logo" 
                style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
              />
            ) : null}
            <span 
              className="text-sm font-medium" 
              style={{ 
                color: 'var(--text-primary)',
                whiteSpace: 'nowrap'
              }}
            >
              {user?.client?.clientName || 'Client'}
            </span>
          </div>
          {/* App Name on separate line */}
          <div className="sidebar-logo" style={{ marginTop: '4px' }}>
            Process Assistant
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {navItems.map((item) => {
            if (item.superAdminOnly && !isSuperAdmin()) return null;
            if (item.adminOnly && !isAdmin()) return null;
            
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                end={item.to === '/'}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        
        <div style={{ padding: '12px', borderTop: '1px solid var(--border)' }}>
          <div className="text-sm" style={{ marginBottom: '8px' }}>
            <strong>{user?.fullName}</strong>
            <div className="text-muted">{user?.role?.roleName}</div>
          </div>
          <button onClick={handleLogout} className="btn btn-secondary w-full">
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
      
      <main className="main-content">
        <div className="page-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;